import { EXECUTION_STATUS } from '../../../../AutomationStudio/hooks/useReplayExecution';
import { getActiveFlatSteps } from '../../../../../features/automationStudioNew/planUtils';

/**
 * Flat replay index for progress map / stepDetails lookup.
 * Prefer plan stepIndex and active flat-step order over UI row offsets.
 */
export function resolveStepIndexForReplayLookup(step, flatStepIndex, flatStepsForReplay = null) {
    if (typeof step?.stepIndex === 'number') {
        return step.stepIndex;
    }
    if (Array.isArray(flatStepsForReplay)) {
        const fromPlan = flatStepsForReplay.find((s) => s?.id === step?.id);
        if (typeof fromPlan?.stepIndex === 'number') {
            return fromPlan.stepIndex;
        }
    }
    return flatStepIndex;
}

/** WebSocket execution flow payload: { executions: [...] } or [] when empty */
export function getExecutionsFromFlow(executionFlow) {
    if (!executionFlow || Array.isArray(executionFlow)) return [];
    return Array.isArray(executionFlow.executions) ? executionFlow.executions : [];
}

/** Active instruction = last execution group's instructionId (matches backend stream order). */
export function getActiveInstructionIdFromFlow(executionFlow, firstInstructionId) {
    const executions = getExecutionsFromFlow(executionFlow);
    if (executions.length === 0) {
        return firstInstructionId ?? null;
    }
    const last = executions[executions.length - 1];
    const main = last?.mainAction;
    const id = main?.instructionId ?? main?.args?.instructionId;
    if (id != null) return id;
    return firstInstructionId ?? null;
}

export function getInstructionsInPlanOrder(instructionsList) {
    return (instructionsList || []).filter(
        (inst) => inst.aiSteps && inst.aiSteps.length > 0
    );
}

export function getActiveInstructionsInPlanOrder(instructionsList) {
    return getInstructionsInPlanOrder(instructionsList).filter(
        (inst) => !inst.removedFromEditor
    );
}

export function findInstructionIdForFlatStepIndex(planOrderInstructions, flatIndex) {
    if (flatIndex == null || flatIndex < 0 || !Array.isArray(planOrderInstructions)) {
        return null;
    }
    let start = 0;
    for (const inst of planOrderInstructions) {
        const n = (inst.aiSteps || []).length;
        if (n === 0) continue;
        if (flatIndex >= start && flatIndex < start + n) {
            return inst.id;
        }
        start += n;
    }
    return null;
}

export function resolveEffectiveReplayFlatIndex(
    replayCurrentStepIndex,
    replayProgressMap,
    replayExecutionResults
) {
    if (replayCurrentStepIndex != null && replayCurrentStepIndex >= 0) {
        return replayCurrentStepIndex;
    }
    let best = -1;
    const rpm = replayProgressMap || {};
    for (const [k, v] of Object.entries(rpm)) {
        if (v === EXECUTION_STATUS.IN_PROGRESS) {
            const n = Number(k);
            if (Number.isFinite(n)) best = Math.max(best, n);
        }
    }
    if (best >= 0) return best;

    const details = replayExecutionResults?.stepDetails || {};
    for (const [k, detail] of Object.entries(details)) {
        if (detail?.status === 'executing') {
            const n = Number(k);
            if (Number.isFinite(n)) best = Math.max(best, n);
        }
    }
    if (best >= 0) return best;
    return null;
}

export function computeReplayHighlightFlatIndex({
    isReplayExecuting,
    instructionsInPlanOrder,
    replayCurrentStepIndex,
    replayProgressMap,
    replayExecutionResults,
}) {
    if (!isReplayExecuting) return null;
    const totalFlat = instructionsInPlanOrder.reduce(
        (acc, inst) => acc + (inst.aiSteps?.length || 0),
        0
    );
    if (totalFlat === 0) return null;
    const resolved = resolveEffectiveReplayFlatIndex(
        replayCurrentStepIndex,
        replayProgressMap,
        replayExecutionResults
    );
    const idx = resolved != null && resolved >= 0 ? resolved : 0;
    return Math.min(Math.max(idx, 0), totalFlat - 1);
}

export function buildPlanFlatStartByInstructionId(instructionsInPlanOrder) {
    const m = new Map();
    let start = 0;
    instructionsInPlanOrder.forEach((inst) => {
        m.set(inst.id, start);
        start += (inst.aiSteps || []).length;
    });
    return m;
}

/** During Generate AI, flat index of the step currently executing (first pending after last finished). */
/** Instructions that produced steps — used for completed execution results UI. */
export function getInstructionsWithStepsForResults(instructions = []) {
    return (instructions || []).filter(
        (inst) => !inst.removedFromEditor && Array.isArray(inst.aiSteps) && inst.aiSteps.length > 0
    );
}

export function captureGenerationBaselineIds(instructions = []) {
    return new Set(
        (instructions || [])
            .filter((inst) => !inst.removedFromEditor)
            .map((inst) => String(inst.id))
    );
}

export function hasInstructionsAddedAfterBaseline(instructions = [], baselineIds = null) {
    if (!baselineIds?.size) return false;
    return (instructions || []).some(
        (inst) => !inst.removedFromEditor && !baselineIds.has(String(inst.id))
    );
}

export function computeGenerationActiveFlatIndex(instructions = [], isGenerating = false) {
    if (!isGenerating) return null;
    const allSteps = getActiveFlatSteps(instructions);
    if (allSteps.length === 0) return 0;
    let lastDone = -1;
    allSteps.forEach((step, index) => {
        if (step.status === 'success' || step.status === 'failed' || step.status === 'completed') {
            lastDone = index;
        }
    });
    const next = lastDone + 1;
    if (next < allSteps.length) return next;
    const last = allSteps[allSteps.length - 1];
    if (last?.status === 'pending' || !last?.status) return allSteps.length - 1;
    return null;
}
